/**
 * Deliberation API Service — War Room Phase C.1
 *
 * TypeScript client wrapping the 6 deliberation REST endpoints.
 * Uses the existing `api` utility from utils/api.ts for auth + error handling.
 */

import { api } from '../utils/api';
import { formatCurrency as canonicalFormatCurrency } from '../utils/financialFormat';
import type { DossierItem, DossierHole } from '../types/methodology';

// =============================================================================
// TYPES
// =============================================================================

export interface ResolutionPath {
  action: string;
  responsible_party: string;
  impact_value: string | null;
  impact_description: string;
  priority: string;
  documents_needed: string[] | null;
  deadline_note: string | null;
  source_agent: string | null;
}

export interface RagChunk {
  content: string;
  source_file: string;
  similarity: number;
}

export interface DeliberationEvent {
  agent_name: string;
  event_type: string; // "tool_call" | "finding" | "position" | "disagreement" | "human_input" | "re_analysis"
  tool_name: string | null;
  tool_params: Record<string, any> | null;
  tool_result_summary: string;
  tool_result_data: { chunks: RagChunk[]; query: string } | null;
  reasoning: string;
  issue_tag: string | null;
  confidence: number | null;
  value: string | null; // Decimal serialized as string
  timestamp: string;
  parent_event_id: string | null;
  resolution_path: ResolutionPath | null;
}

export interface AgentPosition {
  value: string | null;
  confidence: number | null;
  reasoning: string;
  tool_calls: string[];
}

export interface IssueResolution {
  method: string;
  value: string | null;
  explanation: string;
}

export interface DisputedIssue {
  tag: string;
  title: string;
  description: string;
  agent_positions: Record<string, AgentPosition>;
  human_inputs: DeliberationEvent[];
  resolution: IssueResolution | null;
  resolution_paths: ResolutionPath[];
  status: string; // "open" | "resolved" | "human_override"
}

export interface DeliberationSummary {
  total_events: number;
  tools_used: number;
  disagreements_found: number;
  human_inputs: number;
  re_analyses_triggered: number;
  total_resolution_impact: string;
}

export interface ConsistencyFinding {
  fact_key: string;
  verdict: 'FACTUAL' | 'STRATEGIC';
  severity: 'blocker' | 'warning' | 'info';
  seats_disagreeing: string[];
  source_value: unknown;
  seat_values: Record<string, unknown>;
  detail: string;
}

/** WS-3 consistency gate output — written onto the deliberation doc post-store.
 * FACTUAL findings are contradictions against the parsed record (endorsement
 * schedule, document inventory, engine outputs); strategic divergence between the
 * seats is never flagged. `blocked` means "would block finalize in enforcing mode". */
export interface ConsistencyReport {
  findings: ConsistencyFinding[];
  factual_count: number;
  blocked: boolean;
  mode?: 'off' | 'advisory' | 'enforcing' | string;
  checked_at?: string;
  seats_checked?: string[];
  strategic_note?: string;
}

export interface CaseDeliberation {
  report_id: string;
  analysis_run_id: string;
  events: DeliberationEvent[];
  issues: DisputedIssue[];
  created_at: string;
  updated_at: string;
  consistency_report?: ConsistencyReport | null;
  consistency_status?: string | null;
  withheld_seats?: string[];
}

export interface GroupedResolutionPaths {
  report_id: string;
  by_party: Record<string, Array<ResolutionPath & { issue_tag: string }>>;
}

export interface HumanInputRequest {
  issue_tag: string;
  content: string;
  author_role: string;
  author_name: string;
}

export interface HumanInputResponse {
  event_created: boolean;
  issue_tag: string;
  issue: DisputedIssue | null;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

const BASE = '/api/v1/deliberation';

/**
 * Get full CaseDeliberation for a report.
 *
 * F2.66: 404 here is a legitimate "no deliberation has run yet" state —
 * suppress the default error toast and let the consumer render its
 * empty-state UI. The WarRoom page already explains the empty state.
 */
export async function getDeliberation(reportId: string): Promise<CaseDeliberation> {
  return api.get<CaseDeliberation>(`${BASE}/${reportId}`, { showErrorToast: false, timeoutMs: 30000 });
}

/**
 * Latest COMPLETED agent-fleet run, reshaped server-side into the same
 * CaseDeliberation shape as {@link getDeliberation} but with FOUR seats — including
 * the adversarial defendant-attorney. WarRoom prefers this so users see their actual
 * fleet results; a 404 (no completed fleet run) is a legitimate "fall back to the
 * classic deliberation" signal, so the error toast is suppressed.
 */
export async function getFleetDeliberation(reportId: string): Promise<CaseDeliberation> {
  return api.get<CaseDeliberation>(`${FLEET_BASE}/${reportId}/deliberation`, { showErrorToast: false, timeoutMs: 30000 });
}

/** Get list of disputed issues for a report. */
export async function getIssues(reportId: string): Promise<{ issues: DisputedIssue[]; count: number }> {
  return api.get(`${BASE}/${reportId}/issues`);
}

/** Get events sorted by timestamp (chronological timeline). */
export async function getTimeline(reportId: string): Promise<{ events: DeliberationEvent[]; count: number }> {
  return api.get(`${BASE}/${reportId}/timeline`);
}

/**
 * Get computed deliberation summary (counts and aggregate stats).
 *
 * F2.66: 404 here is a legitimate "no deliberation has run yet" state —
 * suppress the default error toast (paired with getDeliberation above).
 */
export async function getSummary(reportId: string): Promise<DeliberationSummary> {
  return api.get<DeliberationSummary>(`${BASE}/${reportId}/summary`, { showErrorToast: false, timeoutMs: 30000 });
}

/** Get resolution paths grouped by responsible_party. */
export async function getResolutionPaths(reportId: string): Promise<GroupedResolutionPaths> {
  return api.get<GroupedResolutionPaths>(`${BASE}/${reportId}/resolution-paths`, { showErrorToast: false });
}

/** Submit human input on an issue, triggering re-analysis. */
export async function submitHumanInput(
  reportId: string,
  input: HumanInputRequest,
): Promise<HumanInputResponse> {
  return api.post<HumanInputResponse>(`${BASE}/${reportId}/input`, input);
}

// =============================================================================
// F7 — TRACKED HUMAN-ESCALATION (12-factor #7)
// Decision-required ActionItems opened by the War Room consensus reducer and
// surfaced on WarRoom.tsx with a resolve action. Reads the action-items API
// (a DIFFERENT base than the deliberation endpoints above).
// =============================================================================

const ACTION_ITEMS_BASE = '/api/v1/action-items';

/** One tracked decision-required escalation (a subset of the additive
 * ActionItemResponse the backend returns; only fields the card renders). */
export interface EscalationItem {
  id: string;
  report_id: string;
  source_agent: string;
  action_type: string;
  urgency: string;
  is_blocking: boolean;
  title: string;
  description: string;
  reason: string;
  status: string; // "pending" | "resolved" | "cancelled" | "escalated" | ...
  created_at: string;
  request_count: number;
  metadata?: Record<string, unknown> | null;
  resolution_notes?: string | null;
  resolved_at?: string | null;
  resolved_by_user_id?: string | null;
  updated_at?: string | null;
}

export interface EscalationListResponse {
  items: EscalationItem[];
  count: number;
  total_blocking: number;
}

/**
 * List decision-required escalations for a report (open + resolved).
 *
 * A 404 here is a legitimate "no escalations / report not visible" state (the
 * action-items endpoints are org-scoped, mirroring the other War Room reads) —
 * suppress the toast and let the card render its empty state.
 */
export async function getEscalations(reportId: string): Promise<EscalationListResponse> {
  return api.get<EscalationListResponse>(
    `${ACTION_ITEMS_BASE}/${reportId}?action_type=decision_required`,
    { showErrorToast: false },
  );
}

/**
 * Resolve a decision-required escalation. Uses the EXISTING action-item resolve
 * endpoint. NOTE the endpoint returns 400 on an already-resolved item (not
 * idempotent) — the caller treats a 400-already-resolved as success + refetch.
 */
export async function resolveEscalation(
  itemId: string,
  notes: string,
): Promise<{ status: string; item_id: string }> {
  return api.post(`${ACTION_ITEMS_BASE}/${itemId}/resolve`, {
    resolution_notes: notes,
  }, { showErrorToast: false });
}

/** Trigger War Room analysis for a report/draft. Returns immediately; analysis runs in background. */
export interface TriggerResponse {
  triggered: boolean;
  status: string;
  run_id: string;
  message: string;
}

export async function triggerAnalysis(reportId: string): Promise<TriggerResponse> {
  return api.post<TriggerResponse>(`${BASE}/${reportId}/trigger`, {});
}

/** Per-agent tool timing summary from war_room_runs.steps.X.tool_timing (Op-FU9 step 4) */
export interface AgentToolTiming {
  tool_count: number;
  error_count: number;
  total_ms: number;
  per_tool: Record<string, { count: number; total_ms: number; error_count: number }>;
}

/** Check status of a running War Room analysis. */
export interface TriggerStatus {
  report_id: string;
  status: string; // "running" | "complete" | "failed" | "no_analysis"
  run_id?: string;
  event_count?: number;
  issue_count?: number;
  error?: string;
  /** Op-FU9 step 4: per-agent tool latency, keyed by agent name (forensic/adjuster/attorney) */
  agent_timing?: Record<string, AgentToolTiming>;
  /** Per-agent step status (present on the MongoDB-backed status path). */
  steps?: { forensic?: string; adjuster?: string; attorney?: string };
  /** Forensic defensible loss range (Monte Carlo P25–P75). */
  forensic_range?: { low?: number | null; high?: number | null };
  /** Forensic point estimate (recommended recovery, e.g. 152461). */
  recommended_recovery?: number | null;
  /** Attorney settlement target. */
  attorney_target?: number | null;
}

export async function getTriggerStatus(reportId: string): Promise<TriggerStatus> {
  // Observability-only: never toast (an external CPA may not be able to read it).
  return api.get<TriggerStatus>(`${BASE}/${reportId}/trigger/status`, { showErrorToast: false });
}

// =============================================================================
// FLEET API
// =============================================================================

const FLEET_BASE = '/api/v1/fleet';

export interface FleetRunResponse {
  run_id: string;
  status: string;
  mode: string;
}

export interface FleetStatus {
  run_id: string;
  report_id: string;
  status: string; // "running" | "completed" | "failed" | "orphaned"
  cards?: Record<string, { state: string; [key: string]: any }>;
  error?: string;
  updated_at?: string;
  /** F1: true if a seat intended MCP engine tools but silently ran the no-tools
   *  legacy path — the run's figures may lack engine grounding. */
  mcp_degraded?: boolean;
  mcp_degraded_reason?: string | null;
}

/** Trigger a fleet run (replaces the old UnifiedAnalysisService trigger). */
export async function runFleet(reportId: string): Promise<FleetRunResponse> {
  return api.post<FleetRunResponse>(`${FLEET_BASE}/${reportId}/run`, {});
}

/** Refresh all calculation engines. MUST be called before runFleet whenever new
 *  documents were uploaded — the fleet's agents read engine_outputs, and stale
 *  outputs silently carry pre-upload figures into a "fresh" run (the exact
 *  failure observed on d6d88aea: Toast 2023 uploaded but the run's
 *  reconciliation still excluded it). Blocks until engines complete (seconds). */
export async function rerunEngines(
  reportId: string,
): Promise<{ engines_run: number; engines_succeeded: number; run_id: string }> {
  return api.post(`${FLEET_BASE}/${reportId}/engine-rerun`, {});
}

/** Poll fleet run status. */
export async function getFleetStatus(reportId: string): Promise<FleetStatus> {
  return api.get<FleetStatus>(`${FLEET_BASE}/${reportId}/status`);
}

// =============================================================================
// CHALLENGE ROUND TYPES
// =============================================================================

export interface Challenge {
  challenge_id: string;
  source_agent: string;
  target_agent: string;
  target_issue_tag: string;
  challenge_type: string;
  severity: string;
  attack_argument: string;
  evidence_cited: string[];
  suggested_counter: string;
  documents_needed: string[];
  dollar_impact: string | null;
  tool_calls: string[];
}

export interface Rebuttal {
  rebuttal_id: string;
  challenge_id: string;
  source_agent: string;
  outcome: string; // "countered" | "acknowledged" | "partially_addressed"
  counter_argument: string;
  additional_evidence: string[];
  remaining_vulnerability: string | null;
  client_action_needed: string | null;
  tool_calls: string[];
}

export interface UnresolvedVulnerability {
  challenge_id: string;
  severity: string;
  challenge_type: string;
  attack_argument: string;
  dollar_impact: string | null;
  what_would_fix_it: string;
}

export interface ClientAction {
  action: string;
  urgency: string;
  source_challenge_id: string;
  dollar_impact: string | null;
  deadline_note: string | null;
}

export interface VulnerabilityAssessment {
  case_strength_score: number;
  challenges_total: number;
  challenges_countered: number;
  challenges_acknowledged: number;
  challenges_partially_addressed: number;
  unresolved_vulnerabilities: UnresolvedVulnerability[];
  client_actions_required: ClientAction[];
  total_amount_at_risk: string;
  estimated_case_improvement: string;
}

export interface ChallengeRoundResult {
  report_id: string;
  analysis_run_id: string;
  challenges: Challenge[];
  rebuttals: Rebuttal[];
  vulnerability_assessment: VulnerabilityAssessment | null;
  challenge_events: DeliberationEvent[];
  rebuttal_events: DeliberationEvent[];
  created_at: string;
}

export interface ChallengeRoundStatus {
  status: string; // "running" | "complete" | "failed" | "no_challenge_round"
  report_id: string;
  challenges_count?: number;
  rebuttals_count?: number;
  case_strength_score?: number;
  error?: string;
}

// =============================================================================
// CHALLENGE ROUND API FUNCTIONS
// =============================================================================

/** Trigger adversarial challenge round. */
export async function triggerChallengeRound(reportId: string): Promise<TriggerResponse> {
  return api.post<TriggerResponse>(`${BASE}/${reportId}/challenge-round`, {});
}

/** Get challenge round results.
 *
 * The backend returns 404 with `detail="No challenge round found for {id}."`
 * when no round has been triggered yet — that's an expected empty-state,
 * not an error. `ChallengesTab` already branches on the 404 in its catch
 * block to render the empty-state CTA; the global toast was the only
 * user-visible regression. Same fix pattern as TS-FU13 used for
 * `/comparative-analysis` (audit/follow-ups.md). */
export async function getChallengeRound(reportId: string): Promise<ChallengeRoundResult> {
  return api.get<ChallengeRoundResult>(
    `${BASE}/${reportId}/challenge-round`,
    { showErrorToast: false },
  );
}

/** Get challenge round status. */
export async function getChallengeRoundStatus(reportId: string): Promise<ChallengeRoundStatus> {
  return api.get<ChallengeRoundStatus>(`${BASE}/${reportId}/challenge-round/status`);
}

/** Get vulnerability assessment only. */
export async function getVulnerabilities(reportId: string): Promise<VulnerabilityAssessment> {
  return api.get<VulnerabilityAssessment>(`${BASE}/${reportId}/vulnerabilities`);
}

// =============================================================================
// WAR ROOM BRIEF — one-page readable brief replacing raw agent JSON
// =============================================================================

export interface BriefDefensibleRange {
  low: number | null;
  high: number | null;
  methodology: string | null;
}

export interface BriefEpi {
  status: string | null;
  total_epi_loss: number | null;
  reason: string | null;
}

/** The headline BI figure: the attorney's ELECTED method's net (code-composed; default = reconciled). */
export interface BriefElectedBi {
  value: number | null;
  method: 'bottom_up' | 'top_down' | 'reconciled';
  basis: string | null;
  provenance?: Record<string, unknown> | null;
  elected_by: 'attorney' | 'default';
}

export interface BriefHeadline {
  /** Absent on runs from before WP5 or when the recovery summary could not be computed. */
  elected_bi?: BriefElectedBi | null;
  supportable_ceiling_label?: string | null;
  bi_total_loss: number | null;
  defensible_range: BriefDefensibleRange;
  epi: BriefEpi;
  recommended_recovery_p50: number | null;
  supportable_ceiling: number | null;
  ceiling_derivation: string | null;
  sworn_net_claim?: number | null;
}

export interface BriefAgreement {
  engine: string;
  position: string | null;
  seats: string[];
}

export interface BriefDispute {
  topic: string;
  seats: string[];
  positions: string | null;
  dollars_at_stake: number | null;
  resolution: string | null;
}

export interface BriefGateFinding {
  fact_key: string;
  severity: 'blocker' | 'warning' | 'info';
  detail: string;
  seats: string[];
}

export interface BriefDefensePreview {
  primary_attack: string | null;
  attack_vector: string | null;
  alternative_damages: number | null;
  daubert_score: number | null;
  leverage_move: string | null;
  mediation_position: number | null;
}

export interface BriefNextAction {
  action: string;
  source_seat: string | null;
  priority: 'high' | 'normal';
}

/** A single missing/needed document reference — shared shape used by both
 * counter_strategy.evidence_needed and methodology_paths.missing_documents. */
export interface BriefDocumentRef {
  requirement_key: string;
  display_name: string;
  document_type: string;
}

export interface BriefCollectionStatus {
  state: 'requested' | 'not_requested' | 'unknown';
  requested_at: string | null;
}

export interface BriefCounterStrategy {
  defense_argument: string;
  attack_type: 'methodology' | 'period_of_restoration' | 'catch_up_sales' | 'non_covered_cause';
  our_counter: string | null;
  counter_gap: boolean;
  evidence_needed: BriefDocumentRef[];
  impact_if_collected: string | null;
  collection_status: BriefCollectionStatus;
  carrier_will_pull: string[];
}

export interface BriefMethodologyPath {
  method_id: string;
  label: string;
  supportable_figure: number | null;
  status: string | null;
  missing_documents: BriefDocumentRef[];
  ready: boolean;
  gap_state?: 'checked' | 'detection_unavailable' | 'not_assessed';
  strengths?: DossierItem[];
  weaknesses?: DossierItem[];
  holes?: DossierHole[];
}

/** One line of the carrier-position reduction ledger (backend
 * war_room_carrier_position.build_carrier_position). Tier 1 = attestable engine
 * composition; tier 3 = carrier's asserted (LLM) reduction — shown, never applied. */
export interface CarrierLedgerLine {
  tier: 1 | 3;
  key: string;
  label: string;
  engine_id?: string;
  run_id?: string | null;
  source?: string;
  delta?: number | null;
  running?: number | null;
  pct?: number | null;
  asserted_value?: number | null;
  would_reduce_by?: number | null;
  argument?: string;
  attestable: boolean;
  export?: boolean;
  note?: string | null;
}

/** One rung of the documentation-contingent mitigation exposure ladder. */
export interface ExposureLadderRung {
  key: string;
  label: string;
  deduction: number;
  deduction_pct?: number | null;
  credited: boolean;
  evidence_cite?: string | null;
  evidence_doc_id?: string | null;
  /** PROJECTION — where the floor lands if this rung + those above it are credited.
   * Never an attested/persisted amount. */
  running_floor_if_credited: number;
}

/** The mitigation exposure ladder (backend war_room_mitigation_ladder.build_mitigation_ladder):
 * floor (net − fail-open credit) → per-factor rungs → net-claim ceiling. Read-only. */
export interface ExposureLadder {
  available: boolean;
  reason?: string;
  ceiling?: number;
  floor?: number;
  mitigation_credit?: number;
  fully_documented?: number;
  open_factor_count?: number;
  rungs?: ExposureLadderRung[];
  note?: string;
}

/**
 * Phase 5 §4 subrogation allocation (engine `l_recon`) — surfaced on the brief ONLY for a
 * confirmed third-party file. Side-aware framing: on the plaintiff side it is the
 * "Anticipated carrier subrogation lien" (work product, `sensitivity: "strategy"`,
 * `export: false` — never a demand/workpaper/PDF/DOCX); on the carrier side it is the
 * carrier's own exportable allocation. All money fields are plain-decimal strings.
 */
export interface SubrogationAllocation {
  key: 'subrogation_allocation';
  label: string;
  engine_id: 'l_recon';
  run_id?: string | null;
  doctrine?: string | null;
  attestable: boolean;
  pro_forma: boolean;
  basis?: string | null;
  total_loss?: string | null;
  insurer_paid?: string | null;
  gross_recovery?: string | null;
  carrier_recovers?: string | null;
  insured_keeps?: string | null;
  fees_applied?: string | null;
  carrier_participated?: boolean | null;
  /** Q1: the attorney elected common-fund fee apportionment AND it reduced the carrier lien
   * (bucket figures already reflect it). Additive — absent on older payloads / when off. */
  fee_apportioned?: boolean | null;
  invariant_holds?: boolean | null;
  policy_entitlement_reference?: Record<string, unknown> | null;
  caveats: string[];
  sensitivity: 'strategy' | 'firm';
  export: boolean;
  visibility: string;
  note?: string | null;
  /** Attorney-facing legal disclaimers conditioned on the doctrine + carrier_participated
   * (Fable-gated 2026-10-10). Describe the actual computation + frame every jurisdiction/policy
   * point as a confirm-item. Additive — absent on older payloads. */
  doctrine_disclaimers?: string[] | null;
}

export interface DefendantPositionSideBySide {
  plaintiff: string | null;
  defendant: string | null;
  delta: string | null;
}

export interface DefendantDefenseRow {
  key: string;
  label: string;
  figure_ref: string | null;
  argument: string | null;
}

export interface DefendantPosition {
  export?: boolean;
  attestable?: boolean;
  sensitivity?: string;
  defendant_quantum?: string | null;
  qualified_range?: { min: string; max: string } | null;
  excluded_methods?: Array<{ name: string; status: string; net: string | null }>;
  property_basis_in_demand?: string;
  acv_total?: number | null;
  rcv_total?: number | null;
  recoverable_depreciation?: number | null;
  extra_expense_in_demand?: boolean;
  collateral_reduced?: boolean;
  collateral_benefits_received?: string | null;
  defendant_after_fault?: DefendantPositionSideBySide;
  uncapped?: DefendantPositionSideBySide;
  policy_limits_figure?: DefendantPositionSideBySide;
  defenses?: DefendantDefenseRow[];
  caveats?: string[];
}

/** The carrier-position reduction ledger surfaced on the War Room brief. */
export interface CarrierPosition {
  hero: {
    value: number | null;
    label: string;
    basis: string;
    attestable: boolean;
    default_mitigation: boolean;
    run_id: string | null;
    run_id_note: string | null;
  };
  ledger: CarrierLedgerLine[];
  /** Additive read-only exposure ladder (2026-09-21). */
  exposure_ladder?: ExposureLadder | null;
  /** Additive §4 subrogation allocation — present only on a confirmed third-party file (Phase 5 M4). */
  subrogation_allocation?: SubrogationAllocation | null;
  /** Additive Phase 6 M3 — defendant-position read-only block (strategy, excluded from exports). */
  defendant_position?: DefendantPosition | null;
  excluded_llm_figures: Record<string, number | string | null> | null;
  provenance: {
    engine_run_ids?: Record<string, string | null>;
    deliberation_run_id?: string | null;
    engines?: string[];
    tier3_source?: string | null;
  };
  caveats: string[];
}

/** One failure-to-mitigate factor in the neutral view's defense itemization — a
 * "lever" the carrier must PROVE (p→1) to move the neutral down by engine_delta.
 * On a bare record every p = 0 (carrier's burden). Backend
 * war_room_mediator_view.build_mediator_view. */
export interface MediatorDefenseFactor {
  key: string;
  label: string;
  /** C_i — the dollar the neutral drops by if this factor is proven (p=1). */
  engine_delta: number | null;
  /** Informational max deduction pct for this factor. */
  engine_max_delta?: number | null;
  /** Evidence-gated probability the factor is proven. 0 on a bare record. */
  p: number;
  /** p·C_i — this factor's contribution to Σ pᵢ·Cᵢ. */
  weighted: number;
  /** "attested" when p>0, else the unattested/engine-default burden message. */
  status: string;
  carrier_must_prove?: string | null;
}

/** The neutral mediator view — actuarial best estimate E[X] = N − Σ pᵢ·Cᵢ.
 * THREE blocks that must never collapse: plaintiff party N, carrier party N−C
 * (their p=1 scenario, never the neutral low), neutral mean (degenerates to N on
 * a bare record). Backend war_room_mediator_view.build_mediator_view. */
export interface MediatorView {
  blocks: {
    plaintiff: {
      label: string;
      value: number | null;
      basis?: string;
      attestable?: boolean;
      run_id?: string | null;
      excluded?: { attorney_supportable_ceiling?: number | null; note?: string };
    };
    carrier: {
      label: string;
      value: number | null;
      /** C — failure-to-mitigate credit at the engine's current/default inputs. */
      credit?: number | null;
      /** C_max — the 30% policy cap on total mitigation credit (the ceiling on C). */
      credit_max?: number | null;
      default_mitigation?: boolean;
      /** Always true — the carrier position is a party scenario, never the neutral low bound. */
      never_neutral_low?: boolean;
      /** N − C_max: the hardest floor mitigation can reach (the 30% cap binds).
       * A hard bound, not necessarily exactly reachable (low-industry-weight cases
       * land the all-factors-proven sum above this cap floor). */
      floor_at_policy_cap?: number | null;
      caveat?: string;
    };
    neutral: {
      label: string;
      value: number | null;
      formula?: string;
      /** "attested" when any evidence-gated p is set, else "none". */
      p_source?: string;
      /** True when Σ pᵢ·Cᵢ = 0 → neutral = N (the carrier's gap is an unproven defense). */
      degenerate?: boolean;
      /** Where the neutral lands if the whole defense is proven (= carrier value). */
      scenario_if_defense_proven?: number | null;
      /** Mean-preserving methodology variance shown BESIDE the neutral, never as a reduction. */
      band?: {
        low: number;
        high: number;
        kind?: string;
        mean_preserving?: boolean;
        note?: string;
      } | null;
      run_id?: string | null;
    };
  };
  /** Gap-2: the SEPARATE first-party property track (L_PROP) — RCV + payable, shown
   * BESIDE the BI neutral, NEVER summed into it (no whole-case blend). null → BI-only
   * (render nothing). Figures are the resolver's, identical to demand/PoL. */
  property_track?: {
    label?: string;
    ledger?: string;
    rcv?: number | null;
    acv?: number | null;
    payable?: number | null;
    payable_basis?: string | null;
    /** True only when a two-estimate (contractor/adjuster) reconciliation artifact exists. */
    reconciled?: boolean;
    /** Present only when reconciled; else null → the UI shows "single-estimate basis". */
    unmatched_line_count?: number | null;
    run_id?: string | null;
    note?: string;
  } | null;
  defense_itemization: MediatorDefenseFactor[];
  /** fair_settlement_p50 / adjuster_value are deliberately null (excluded); defendant
   * LLM dollars are advocacy cross-checks, never composed into any block. */
  excluded_figures: Record<string, number | string | null>;
  neutrality_check: {
    low_anchor_is_default_defense?: boolean;
    uses_fair_settlement?: boolean;
    uses_llm_dollars?: boolean;
    p_source?: string;
    symmetry?: string;
    degenerate?: boolean;
  };
  provenance?: {
    engine_run_ids?: Record<string, string | null>;
    deliberation_run_id?: string | null;
    engines?: string[];
    p_source?: string;
    /** Gap-2: role(s) that attested the mitigation levers (from p_proven.set_by);
     * null on a bare record → the provenance line shows "—". */
    attestor?: string[] | null;
    /** Gap-2: marks the mediator view as attorney/forensic work product. */
    work_product?: boolean;
  };
  caveats: string[];
}

export interface Brief {
  report_id: string;
  run_id: string | null;
  generated_at: string;
  headline: BriefHeadline;
  agreements: BriefAgreement[];
  disputes: BriefDispute[];
  gate_findings: BriefGateFinding[];
  defense_preview: BriefDefensePreview | null;
  next_actions: BriefNextAction[];
  caveats: string[];
  sections_omitted: string[];
  /** Optional — absent on v1 payloads. Per-attack counter-strategy cards. */
  counter_strategy?: BriefCounterStrategy[];
  /** Optional — absent on pre-2026-09-10 payloads. Carrier-position reduction ledger. */
  carrier_position?: CarrierPosition | null;
  /** Optional — absent on pre-2026-09-10 payloads. Neutral mediator best-estimate view. */
  mediator_view?: MediatorView | null;
  /** Optional — absent on v1 payloads. Candidate damages methodologies and their readiness. */
  methodology_paths?: BriefMethodologyPath[];
  /** Optional — absent on v1 payloads. POST target for requesting missing documents.
   * null/undefined means the backend has no request flow wired up for this report;
   * the UI must not fabricate an endpoint in that case. */
  document_request_endpoint?: string | null;
  /** Optional — present ONLY when the firm's `client_side === "carrier"` (server-resolved).
   * Pure side-aware FRAMING: the carrier floor leads, the plaintiff net is the opposing
   * position. No figure is recomputed by side — absent on every plaintiff brief, so the
   * plaintiff render stays byte-identical. See war_room_brief_service.build_brief. */
  carrier_emphasis?: CarrierEmphasis | null;
}

/** Side-aware framing block (carrier only). Figures are READ from carrier_position /
 *  mediator_view, never recomputed. */
export interface CarrierEmphasis {
  side: 'carrier';
  lead: {
    label: string;
    value: number | null;
    source: string;
    attestable: boolean;
    default_mitigation: boolean;
  };
  opposing: {
    label: string;
    value: number | null;
    source: string;
  };
  note: string;
}

export interface BriefResponse {
  brief: Brief;
  markdown: string;
}

/**
 * Get the War Room Brief — a readable one-page summary replacing raw agent JSON.
 * 404 is a legitimate "no brief available yet" state (mirrors getDeliberation/
 * getSummary) — suppress the default error toast and let the consumer render
 * its own empty state.
 */
export async function getBrief(reportId: string): Promise<BriefResponse> {
  return api.get<BriefResponse>(`${BASE}/${reportId}/brief`, { showErrorToast: false });
}

/**
 * Download the brief as raw markdown and trigger a client-side file save.
 * `apiFetch` only auto-decodes to Blob for pdf/octet-stream/vnd content types
 * (see utils/api.ts) — a markdown response falls through to `.text()`, so we
 * wrap it in a Blob ourselves before creating the object URL.
 */
export async function downloadBriefMarkdown(reportId: string): Promise<void> {
  const text = await api.get<string>(`${BASE}/${reportId}/brief?format=md`, {
    showErrorToast: true,
  });
  const blob = new Blob([text], { type: 'text/markdown' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `war-room-brief-${reportId}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export interface RequestBriefDocumentsResponse {
  requested: boolean;
  [key: string]: unknown;
}

/**
 * Request missing documents for a brief. The POST target is NOT hardcoded here —
 * it comes from `Brief.document_request_endpoint`, which the backend authors per
 * report (it may not exist at all on a v1 payload, in which case callers must not
 * invoke this at all). `methodIds`, when provided, scopes the request to specific
 * `methodology_paths[].method_id` entries; omit it to request everything outstanding.
 */
export async function requestBriefDocuments(
  endpoint: string,
  methodIds?: string[],
): Promise<RequestBriefDocumentsResponse> {
  return api.post<RequestBriefDocumentsResponse>(
    endpoint,
    methodIds && methodIds.length > 0 ? { method_ids: methodIds } : {},
  );
}

// =============================================================================
// HELPERS
// =============================================================================

/** Get agent display name from agent_name. */
export function getAgentDisplayName(agentName: string): string {
  const map: Record<string, string> = {
    'ai:forensic': 'Forensic Analyst',
    'ai:attorney': 'Attorney Advisor',
    'ai:adjuster': 'Insurance Adjuster',
    'ai:defendant_attorney': 'Defense Counsel',
    'ai:document_auditor': 'Document Auditor',
    'ai:data_specialist': 'Data Specialist',
    'human:accountant': 'Accountant',
    'human:attorney': 'Attorney',
    'human:adjuster': 'Adjuster',
  };
  return map[agentName] || agentName;
}

/**
 * What each seat's headline score actually measures. The generic "confidence" label made
 * a 0–100 case-strength score read like a failing school grade; these name the real metric
 * (and make clear the adjuster/defense numbers are the OTHER side's strength).
 */
export function getMetricLabel(agentName: string): string {
  if (agentName.startsWith('ai:forensic')) return 'Claim Integrity';
  if (agentName.startsWith('ai:defendant_attorney')) return 'Attack Strength';
  if (agentName.startsWith('ai:attorney')) return 'Case Strength';
  if (agentName.startsWith('ai:adjuster')) return 'Carrier Position';
  if (agentName.startsWith('ai:document_auditor')) return 'Doc Health';
  if (agentName.startsWith('ai:data_specialist')) return 'Data Readiness';
  return 'Score';
}

/** Get event type display label. */
export function getEventTypeLabel(eventType: string): string {
  const map: Record<string, string> = {
    'tool_call': 'Tool Call',
    'finding': 'Finding',
    'position': 'Position',
    'disagreement': 'Disagreement',
    'human_input': 'Human Input',
    're_analysis': 'Re-Analysis',
  };
  return map[eventType] || eventType;
}

// =============================================================================
// WAR ROOM COUNTERPART MAPPING — route each AI seat's brief to its human role
// =============================================================================

/** Human workspace roles that have a War Room counterpart seat. Kept as a local
 * union (NOT imported from the stage registry) to avoid a services→components
 * import cycle; keep in sync with `WorkspaceRole` in stage-strip/stageRegistry. */
export type CounterpartRole = 'accountant' | 'attorney' | 'adjuster' | 'data_specialist';

/** Human role → the AI seat(s) whose brief is that role's own counterpart.
 * data_specialist owns TWO seats (Data Readiness + Doc Health). */
export const WARROOM_COUNTERPARTS: Record<CounterpartRole, string[]> = {
  accountant: ['ai:forensic'],
  attorney: ['ai:attorney'],
  adjuster: ['ai:adjuster'],
  data_specialist: ['ai:data_specialist', 'ai:document_auditor'],
};

/** Roles that also see the adversarial carrier-attorney "attack preview". Either
 * side may be the client, so both attorney and adjuster get it (owner 2026-09-09). */
export const WARROOM_ADVERSARY: Partial<Record<CounterpartRole, string>> = {
  attorney: 'ai:defendant_attorney',
  adjuster: 'ai:defendant_attorney',
};

// -----------------------------------------------------------------------------
// SIDE-AWARE SEAT MODEL (Fable gate 2026-09-10)
// The client-side (plaintiff vs carrier) is a property of the DEPLOYMENT, not the
// case — every completed fleet run already carries both sides' payloads. This maps
// each human role to the AI seats it should lead with FOR THAT SIDE. Plaintiff is
// byte-identical to WARROOM_COUNTERPARTS/WARROOM_ADVERSARY above; carrier flips the
// attorney/adjuster framing (defendant_attorney becomes the hero/ally, ai:attorney
// becomes the adversary). Nothing consumes `seatMapFor` yet — wiring is a later step.
// -----------------------------------------------------------------------------

export type ClientSide = 'plaintiff' | 'carrier';

/** Which side this deployment serves. Tenant/env-level, NOT a per-case field
 * (per-case would cross the CT-0 security-writer boundary). Defaults plaintiff. */
export function getClientSide(): ClientSide {
  try {
    const v = (import.meta as any)?.env?.VITE_CLIENT_SIDE;
    return v === 'carrier' ? 'carrier' : 'plaintiff';
  } catch {
    return 'plaintiff';
  }
}

export interface SeatMap {
  hero: string[];
  ally?: string;
  adversary?: string;
}

/** Plaintiff mapping — byte-identical to WARROOM_COUNTERPARTS + WARROOM_ADVERSARY. */
export const PLAINTIFF_SEAT_MAP: Record<CounterpartRole, SeatMap> = {
  accountant: { hero: ['ai:forensic'] },
  attorney: { hero: ['ai:attorney'], adversary: 'ai:defendant_attorney' },
  adjuster: { hero: ['ai:adjuster'], adversary: 'ai:defendant_attorney' },
  data_specialist: { hero: ['ai:data_specialist', 'ai:document_auditor'] },
};

/** Carrier mapping — attorney/adjuster lead with the carrier voice; ai:attorney
 * (plaintiff) becomes the adversary. Accountant stays neutral (ai:forensic feeds
 * both sides); data_specialist is side-agnostic. */
export const CARRIER_SEAT_MAP: Record<CounterpartRole, SeatMap> = {
  accountant: { hero: ['ai:forensic'] },
  attorney: { hero: ['ai:defendant_attorney'], adversary: 'ai:attorney' },
  adjuster: { hero: ['ai:adjuster'], ally: 'ai:defendant_attorney', adversary: 'ai:attorney' },
  data_specialist: { hero: ['ai:data_specialist', 'ai:document_auditor'] },
};

/** The seat map for a role on the given side (defaults to the deployment's side). */
export function seatMapFor(role: CounterpartRole, side: ClientSide = getClientSide()): SeatMap {
  return (side === 'carrier' ? CARRIER_SEAT_MAP : PLAINTIFF_SEAT_MAP)[role];
}

/** One AI seat's brief-card, derived from its deliberation events. */
export interface SeatCard {
  seat: string;
  displayName: string;
  metricLabel: string;
  score: number | null; // last event confidence × 100, rounded
  value: string | null; // last event headline $ (Decimal serialized as string)
  reasoning: string; // last event narrative
  findings: string[]; // up to 6 'finding'-event narratives
  blocked: boolean; // this seat raised a brief gate blocker
  present: boolean; // the seat returned at least one event
}

/**
 * Extract one {@link SeatCard} per distinct `agent_name` across the deliberation
 * events. Pure — no fetching. Seats are the set of `agent_name` values over ALL
 * events (there is NO dedicated 'agent_response' event_type in this system); a
 * seat's headline is its LAST event, its findings are its 'finding' events. Only
 * fields on the existing DeliberationEvent/Brief interfaces are read. Mirrors the
 * `seatRows` derivation already used by the accountant WarRoomReviewContent.
 */
export function extractSeatCards(
  deliberation: CaseDeliberation | null,
  brief?: Brief | null,
): Record<string, SeatCard> {
  const out: Record<string, SeatCard> = {};
  const events = deliberation?.events || [];
  const blockerSeats = new Set(
    (brief?.gate_findings || [])
      .filter((f) => f.severity === 'blocker')
      .flatMap((f) => f.seats || []),
  );
  const seats = Array.from(new Set(events.map((e) => e.agent_name).filter(Boolean)));
  for (const seat of seats) {
    const evs = events.filter((e) => e.agent_name === seat);
    if (evs.length === 0) continue;
    const last = evs[evs.length - 1];
    const narrative = (last.reasoning || '').trim();
    // Dedupe: a 'finding' event whose text is the narrative verbatim would render
    // twice (narrative row + findings list) — drop it (K3 §5 row 4).
    const findings = evs
      .filter((e) => e.event_type === 'finding' && (e.reasoning || '').trim())
      .map((e) => e.reasoning.trim())
      .filter((f) => f !== narrative)
      .slice(0, 6);
    out[seat] = {
      seat,
      displayName: getAgentDisplayName(seat),
      metricLabel: getMetricLabel(seat),
      score: last.confidence != null ? Math.round(last.confidence * 100) : null,
      value: last.value,
      reasoning: narrative,
      findings,
      blocked: blockerSeats.has(seat),
      present: true,
    };
  }
  return out;
}

/** Status of a role's counterpart view, derived from run state + seat presence. */
export type CounterpartStatus = 'none' | 'running' | 'partial' | 'complete';

/** The role-scoped counterpart slice a dashboard's War Room stage renders. */
export interface CounterpartBrief {
  status: CounterpartStatus;
  heroSeats: SeatCard[];
  adversarySeat: SeatCard | null;
  brief: Brief | null;
  missingSeats: string[];
  runId: string | null;
  completedAt: string | null;
}

// =============================================================================
// GAME THEORY — War Room seeded analysis
// =============================================================================

export interface WarRoomGameTheoryContext {
  draft_id: string;
  run_id: string;
  produced_at: string;
  negotiation_style_used: string;
  policy_cap: number | null;
  over_cap_probability_pct: number | null;
  mc_p50_cap_clipped: boolean;
  mc_p50_note: string | null;
  input_sources: {
    claim_value: string;
    adjuster_value: string;
    attorney_value: string;
    monte_carlo: string;
  };
  all_positions: Record<string, number>;
}

export interface GameTheorySettlementRange {
  floor: number;
  ceiling: number;
  nash_equilibrium: number;
  recommended_target: number;
}

export interface GameTheoryZopa {
  exists: boolean;
  range: number;
  floor: number;
  ceiling: number;
  midpoint: number;
  analysis: string;
}

export interface GameTheoryNashEquilibrium {
  point: number;
  claimant_surplus: number;
  insurer_surplus: number;
  fairness_index: number;
  stability: string;
  explanation: string;
}

export interface GameTheoryRecommendation {
  priority: number;
  category: string;
  title: string;
  recommendation: string;
  rationale?: string;
}

export interface WarRoomGameTheoryResult {
  analysis_id: string;
  analyzed_at: string;
  optimal_settlement: number;
  settlement_range: GameTheorySettlementRange;
  zopa: GameTheoryZopa;
  nash_equilibrium: GameTheoryNashEquilibrium;
  strategic_recommendations: GameTheoryRecommendation[];
  war_room_context: WarRoomGameTheoryContext;
}

/**
 * Run game theory analysis seeded from the latest War Room consensus doc.
 * Returns 404 if no War Room run exists, 422 if sensitivity_report unavailable.
 */
export async function getWarRoomGameTheory(
  draftId: string,
  negotiationStyle = 'competitive',
): Promise<WarRoomGameTheoryResult> {
  return api.get<WarRoomGameTheoryResult>(
    `/api/v1/game-theory/war-room/${draftId}?negotiation_style=${negotiationStyle}`,
    { showErrorToast: false },
  );
}

// =============================================================================
// HELPERS
// =============================================================================

/** Get agent color class for styling. */
export function getAgentColor(agentName: string): string {
  if (agentName.startsWith('ai:forensic')) return 'blue';
  if (agentName.startsWith('ai:defendant_attorney')) return 'red';
  if (agentName.startsWith('ai:attorney')) return 'purple';
  if (agentName.startsWith('ai:adjuster')) return 'amber';
  if (agentName.startsWith('human:')) return 'green';
  return 'gray';
}

/** Format a decimal value string as currency. */
export function formatCurrency(value: string | null): string {
  return canonicalFormatCurrency(value);
}
